import { Dtos } from "@inrev/common";
import { match } from "ts-pattern";
import z from "zod";

type ModelSize = Dtos.SuretyAccount.Get.Response["modelSize"];

// Flat shape, matching the edit form and the Update.History.Request body it submits.
export const commercialOnlyHistorySchema = Dtos.SuretyAccount.baseContractHistorySchema.pick({
	causedSuretyLoss: true,
});

// Nested shape, matching the account metadata the history is read back as. Partial-field paths
// are derived from the schema and then looked up on the data, so the two have to agree.
// Specialty codes live on the junction, not metadata, so they are omitted here.
const asMetadataShape = (contractSchema: z.ZodObject) =>
	z.object({
		causedSuretyLoss: Dtos.SuretyAccount.baseContractHistorySchema.shape.causedSuretyLoss,
		contract: contractSchema.nullish(),
	});

// causedSuretyLoss is shared with commercial, so it sits at the metadata top level, not under contract.
// Specialty codes live on the junction, not metadata.
const withoutSharedFields = <T extends z.ZodObject>(schema: T) =>
	schema.omit({ causedSuretyLoss: true });

export const metadataSchemaForAccountDetail = (
	modelSize: NonNullable<ModelSize>,
	showContractHistory: boolean,
) => {
	if (!showContractHistory) return asMetadataShape(z.object({}));

	return match(modelSize)
		.with("small", () =>
			asMetadataShape(withoutSharedFields(Dtos.SuretyAccount.baseContractHistorySchema)),
		)
		.with("medium", () =>
			asMetadataShape(withoutSharedFields(Dtos.SuretyAccount.fullContractHistorySchema)),
		)
		.with("large", () =>
			asMetadataShape(withoutSharedFields(Dtos.SuretyAccount.fullContractHistorySchema)),
		)
		.exhaustive();
};
